import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormData } from "@/stores/formStore";
import { useOccupations } from "@/hooks/useOccupations";
import { useResetInvalidOccupation } from "@/hooks/useResetInvalidOccupation";

import IndexFormFields from "./IndexFormFields";

const IndexForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("widgets");
  const {
    formData,
    updateDetails,
    updateOccupationSkills,
    updatePersonalSkills,
    updateSpecializationInputs
  } = useFormData();

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    age?: string;
    occupation?: string;
    residence?: string;
    birthplace?: string;
  }>({});

  const [localFormData, setLocalFormData] = useState({
    name: formData.details.name,
    age: formData.details.age,
    occupation: formData.details.occupation,
    residence: formData.details.residence,
    birthplace: formData.details.birthplace,
    avatar: formData.details.avatar,
    maxSkillCapacity: formData.details.maxSkillCapacity || 75,
  });

  const { data: eraOccupations, isLoading: isLoadingEraOccupations } = useOccupations(
    formData.era,
    formData.socialClass,
  );
  useResetInvalidOccupation(eraOccupations, isLoadingEraOccupations, () => {
    setLocalFormData((prev) => ({ ...prev, occupation: "" }));
  });

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    setLocalFormData({
      ...localFormData,
      [field]: value,
    });

    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

  };

  const handleAvatarChange = (avatarDataUrl: string) => {
    setLocalFormData(prev => ({
      ...prev,
      avatar: avatarDataUrl
    }));
  };

  const handleOccupationChange = (value: string) => {
    setLocalFormData(prev => ({
      ...prev,
      occupation: value
    }));

    if (validationErrors.occupation) {
      setValidationErrors(prev => ({
        ...prev,
        occupation: undefined
      }));
    }
  };

  const handleMaxSkillCapacityChange = (value: string) => {
    setLocalFormData(prev => ({
      ...prev,
      maxSkillCapacity: parseInt(value)
    }));
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};
    
    if (!localFormData.name.trim()) {
      errors.name = t("details.errors.nameRequired");
    }
    
    if (!localFormData.residence.trim()) {
      errors.residence = t("details.errors.residenceRequired");
    }
    
    if (!localFormData.birthplace.trim()) {
      errors.birthplace = t("details.errors.birthplaceRequired");
    }

    if (!localFormData.occupation) {
      errors.occupation = t("details.errors.occupationRequired");
    }

    if (!localFormData.age.trim()) {
      errors.age = t("details.errors.ageRequired");
    } else {
      const ageNum = Number(localFormData.age);
      if (isNaN(ageNum)) {
        errors.age = t("details.errors.ageNumber");
      } else if (ageNum < 15 || ageNum > 90) {
        errors.age = t("details.errors.ageRange");
      }
    }
    
    setValidationErrors(errors);
    
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const occupationChanged =
      Boolean(formData.details.occupation) &&
      formData.details.occupation !== localFormData.occupation;

    if (occupationChanged) {
      updateOccupationSkills({});
      updatePersonalSkills({});
      updateSpecializationInputs({});
    }

    updateDetails(localFormData);
    navigate(formData.pulpMode ? '/archetype' : '/backstory');
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-[520px]">
      <IndexFormFields
        formData={localFormData}
        validationErrors={validationErrors}
        handleInputChange={handleInputChange}
        handleOccupationChange={handleOccupationChange}
        handleAvatarChange={handleAvatarChange}
        handleMaxSkillCapacityChange={handleMaxSkillCapacityChange}
      />

      <div className="flex w-full max-w-[472px] mx-auto items-center gap-4 text-base text-primary-foreground font-normal whitespace-nowrap leading-none mt-8">
        <button
          type="submit"
          className="self-stretch rounded-[8px] border-[length:var(--sds-size-stroke-border)] border-[color:var(--sds-color-border-brand-default)] bg-brand-cream hover:opacity-90 min-w-60 w-full gap-2 overflow-hidden flex-1 shrink basis-[0%] my-auto p-3 border-solid max-md:max-w-full transition-opacity"
        >
          {t("buttons.continue", { ns: "flow" })}
        </button>
      </div>
    </form>
  );
};

export default IndexForm;
